import { Formik } from 'formik'
import { useState, useEffect } from 'react'
import {
  Button,
  Container,
  Form,
  Label,
  Message,
  Search,
} from 'semantic-ui-react'
import * as yup from 'yup'
import Customer from '../../models/customer'
import { useHistory } from 'react-router-dom'
import PurchaseOrder from '../../models/purchase_order'
import _ from 'lodash'
import Vendor from '../../models/vendor'
import User from '../../models/user'
import Note from '../../models/note'
import { VendorSearch } from '../../components/VendorSearch/vendorSearch'
import { ManufacturerSearch } from '../../components/ManufacturerSearch/manufacturerSearch'

let schema = yup.object().shape({
  customer: yup.object().required(),
  user: yup.object().nullable(),
  vendor: yup.object().nullable(),
  manufacturer: yup.object().nullable(),
})

const initialValues: any = {
  vendor: {},
  manufacturer: {},
  customer: {},
  user: {},
  note: null,
}

export default function PurchaseOrderNew() {
  const history = useHistory()
  const [success, setSuccess] = useState<any>(null)

  function handleSuccessToast(po: any) {
    setSuccess(po.id)
    setTimeout(() => {
      setSuccess(null)
    }, 6000)
  }

  function goToPurchaseOrder(po: any) {
    history.push(`/purchase-orders/${po}`)
  }

  return (
    <Container>
      {success && (
        <Message
          success
          header="Success!"
          content={
            <Button primary onClick={() => goToPurchaseOrder(success)}>
              Your purchase order number is: {success}
            </Button>
          }
        />
      )}
      <h2>Create a new Purchase Order</h2>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={(values: any, { setSubmitting, resetForm }) => {
          setTimeout(async () => {
            let po: any = new PurchaseOrder()
            po.customer = new Customer({ id: values.customer.id })
            po.customer.isPersisted = true

            // if (values.job?.id) {
            //   let j = new Job({id: values.job.id})
            //   j.isPersisted = true
            //   po.jobs.push(j)
            // }

            if (values.vendor?.id) {
              po.vendor = new Vendor({ id: values.vendor?.id })
              po.vendor.isPersisted = true
            }
            if (values.manufacturer?.id) {
              po.manufacturer = new Vendor({ id: values.manufacturer?.id })
              po.manufacturer.isPersisted = true
            }
            if (values.user?.id) {
              po.user = new User({ id: values.user?.id })
              po.user.isPersisted = true
            }

            if (values.note) {
              let note = new Note()
              note.text = values.note
              po.notes.push(note)
            }

            const success = await po.save({
              with: [
                'vendor.id',
                'user.id',
                'notes',
                'customer.id',
                'manufacturer.id',
              ],
            })
            if (success) {
              setSubmitting(false)
              resetForm(initialValues)
              handleSuccessToast(po)
            }
          }, 400)
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }: /* and other goodies */
        any) => (
          <Form onSubmit={handleSubmit}>
            <Form.Field required>
              <label>Customer: {values.customer?.name}</label>
              <CustomerSearch
                onChange={setFieldValue}
                value={values.customer?.name}
              />
            </Form.Field>
            <Form.Field>
              <label>Vendor: {values.vendor?.name}</label>
              <VendorSearch
                onChange={setFieldValue}
                value={values.vendor?.name}
              />
            </Form.Field>
            <Form.Field>
              <label>Manufacturer: {values.manufacturer?.name}</label>
              <ManufacturerSearch
                onChange={setFieldValue}
                value={values.manufacturer?.name}
              />
            </Form.Field>
            <Form.Field>
              <label>Salesman: {values.user?.name}</label>
              <UserSearch onChange={setFieldValue} value={values.user?.name} />
            </Form.Field>
            <Form.Field
              name="note"
              label="Note:"
              control="textarea"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.note || ''}
              error={errors.note && touched.note && errors.note}
            />

            <Button primary type="submit" disabled={isSubmitting}>
              Submit
            </Button>
          </Form>
        )}
      </Formik>
    </Container>
  )
}

function UserSearch(props: any) {
  const [loading] = useState<any>(false)
  const [results, setResults] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState<any>('')
  const { onChange } = props

  useEffect(() => {
    getResults()
  }, [searchTerm])

  const resultRenderer = ({ name }: any) => <Label content={name} />

  async function getResults() {
    let { data: userData }: any = await User.where({
      user_type: 'employee',
      status: 'active',
      name: { fuzzyMatch: searchTerm.replace('&', '') },
    }).all()
    setResults(userData)
  }
  const handleSearchChange = _.debounce(async (e: any, data: any) => {
    if (props.value) onChange('user', {})
    setSearchTerm(data.value)
  }, 600)

  function handleSelectItem(e: any, data: any) {
    onChange('user', data.result)
    setSearchTerm('')
  }

  return (
    <Search
      loading={loading}
      onResultSelect={handleSelectItem}
      onSearchChange={handleSearchChange}
      resultRenderer={resultRenderer}
      results={results}
      value={props.value}
      placeholder="Enter a user name"
    />
  )
}

function CustomerSearch(props: any) {
  const [loading] = useState<any>(false)
  const [results, setResults] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState<any>('')
  const { onChange } = props

  useEffect(() => {
    getResults()
  }, [searchTerm])

  const resultRenderer = ({ name }: any) => <Label content={name} />

  async function getResults() {
    if (!searchTerm) return
    let { data: customerData }: any = await Customer.includes('salesman')
      .where({
        name: { fuzzyMatch: searchTerm.replace('&', '') },
      })
      .all()
    setResults(customerData)
  }
  const handleSearchChange = _.debounce(async (e: any, data: any) => {
    if (props.value) onChange('customer', {})
    setSearchTerm(data.value)
  }, 600)

  function handleSelectItem(e: any, data: any) {
    onChange('customer', data.result)
    if (data.result.salesman) onChange('user', data.result.salesman)
    setSearchTerm('')
  }

  return (
    <Search
      loading={loading}
      onResultSelect={handleSelectItem}
      onSearchChange={handleSearchChange}
      resultRenderer={resultRenderer}
      results={results}
      value={props.value}
      placeholder="Enter a customer name"
    />
  )
}
